import type { DragEndEvent } from '@dnd-kit/core'
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { LessonOrderItem } from '@lms/shared'
import { GripVertical } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { useCourseLessonOrder, useUpdateLessonOrder } from '@/hooks/use-course-lesson-order'

function SortableLesson({ lesson }: { lesson: LessonOrderItem }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: lesson.id,
  })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 rounded-md border bg-background px-3 py-2"
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="cursor-grab text-muted-foreground active:cursor-grabbing"
        aria-label="ドラッグして並び替え"
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <div className="flex-1 text-sm">
        <span className="font-medium">{lesson.title}</span>
        <span className="ml-2 text-xs text-muted-foreground">{lesson.level}</span>
      </div>
    </div>
  )
}

interface LessonOrderEditorProps {
  courseId: string
}

export function LessonOrderEditor({ courseId }: LessonOrderEditorProps) {
  const { data: remoteList, isLoading } = useCourseLessonOrder(courseId)
  const updateOrder = useUpdateLessonOrder(courseId)
  const [items, setItems] = useState<LessonOrderItem[]>([])

  useEffect(() => {
    if (remoteList) {
      setItems(remoteList)
    }
  }, [remoteList])

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    setItems((prev) => {
      const oldIndex = prev.findIndex((item) => item.id === active.id)
      const newIndex = prev.findIndex((item) => item.id === over.id)
      const next = [...prev]
      const [moved] = next.splice(oldIndex, 1)
      next.splice(newIndex, 0, moved)
      return next
    })
  }

  const handleSave = async () => {
    try {
      await updateOrder.mutateAsync({ lessonIds: items.map((i) => i.id) })
      toast.success('並び順を保存しました')
    } catch {
      toast.error('保存に失敗しました')
    }
  }

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">読み込み中...</p>
  }

  if (items.length === 0) {
    return <p className="text-sm text-muted-foreground">レッスンがまだありません</p>
  }

  return (
    <div className="space-y-3">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {items.map((lesson) => (
              <SortableLesson key={lesson.id} lesson={lesson} />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleSave}
        disabled={updateOrder.isPending}
      >
        {updateOrder.isPending ? '保存中...' : '並び順を保存'}
      </Button>
    </div>
  )
}
